import React, { useRef, useState, useEffect } from "react";
import { useParams } from "react-router";
import { Formik, Form } from "formik";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Input from "src/components/form/Input";
import useUnsavedChanges from "src/hooks/useUnsavedChanges";
import UnsavedChangesDialog from "../../modals/UnsavedChanges/UnsavedChangesDialog";
import * as Yup from "yup";
import { useMutation } from "@apollo/client/react";
import {
  CREATE_COST_CENTER_MUTATION,
  UPDATE_COST_CENTER_MUTATION,
} from "src/constants/graphql/mutations";
import { CLIENTS_AND_PROJECTS_QUERY, FETCH_PROJECT_QUERY } from "src/constants/graphql/queries";
import useNotification from "src/hooks/notification";

const EMAIL_LIST_FIELDS = [
  { name: "generalNotificationsEmailSendList", label: "General Notifications Email List" },
  { name: "approvedEmailSendList", label: "Notify Customer After Approval Email List" },
  { name: "automatedReportsEmailSendList", label: "Automated Reports Email List" },
  { name: "outboundOnlyEmailSendList", label: "Outbound Only Email List" },
  { name: "osdEmailSendList", label: "OS&D Only Email List" },
];

function hasAnyEmailListValue(costCenter) {
  if (!costCenter || typeof costCenter !== "object") return false;
  return EMAIL_LIST_FIELDS.some(({ name }) => {
    const v = costCenter[name];
    return v != null && String(v).trim() !== "";
  });
}

const validationSchema = Yup.object({
  costCenterName: Yup.string().required("Cost center name is required"),
  costCenterCode: Yup.string(),
  comments: Yup.string(),
});

export default function CostCenterModal({
  open,
  onClose,
  costCenter = {},
  isNew = false,
  onSaved,
  projectId: propProjectId,
  readOnly = false,
}) {
  const { projectId: routeProjectId } = useParams();
  const projectId = propProjectId || routeProjectId;
  const { notifyError } = useNotification();
  const [isDisabled, setIsDisabled] = useState(Boolean(costCenter?.disabled));
  const [emailSectionExpanded, setEmailSectionExpanded] = useState(() =>
    hasAnyEmailListValue(costCenter)
  );

  const formikRef = useRef(null);

  useEffect(() => {
    if (open) setEmailSectionExpanded(hasAnyEmailListValue(costCenter));
  }, [open, costCenter?.id]);

  useEffect(() => {
    setIsDisabled(Boolean(costCenter?.disabled));
  }, [costCenter?.id, costCenter?.disabled]);

  const {
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    confirmSave,
    showDialog,
    saving: unsavedSaving,
  } = useUnsavedChanges({
    onClose,
    isDirtyFn: () => !!formikRef.current?.dirty,
    onSave: async () => {
      await formikRef.current?.submitForm();
    },
  });

  const [createCostCenter] = useMutation(CREATE_COST_CENTER_MUTATION, {
    refetchQueries: [
      { query: CLIENTS_AND_PROJECTS_QUERY },
      { query: FETCH_PROJECT_QUERY, variables: { id: projectId } },
    ],
  });
  const [updateCostCenter] = useMutation(UPDATE_COST_CENTER_MUTATION, {
    refetchQueries: [
      { query: CLIENTS_AND_PROJECTS_QUERY },
      { query: FETCH_PROJECT_QUERY, variables: { id: projectId } },
    ],
  });

  const emailListVariables = (values) => ({
    generalNotificationsEmailSendList: values.generalNotificationsEmailSendList || "",
    osdEmailSendList: values.osdEmailSendList || "",
    approvedEmailSendList: values.approvedEmailSendList || "",
    outboundOnlyEmailSendList: values.outboundOnlyEmailSendList || "",
    automatedReportsEmailSendList: values.automatedReportsEmailSendList || "",
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (isNew) {
        const { data } = await createCostCenter({
          variables: {
            projectId,
            costCenterName: values.costCenterName,
            costCenterCode: values.costCenterCode,
            comments: values.comments,
            useProjectBillingConfiguration: values.useProjectBillingConfiguration,
            minimumAssetImages: parseInt(values.minimumAssetImages, 10) || 0,
            ...emailListVariables(values),
          },
        });
        const created = data?.createCostCenter?.costCenter;
        if (!created) {
          notifyError(
            data?.createCostCenter?.errors?.map(({ message }) => message).join(", ") ||
              "Failed to create cost center"
          );
          return;
        }
        if (created && onSaved) onSaved(created);
      } else {
        const { data } = await updateCostCenter({
          variables: {
            id: values.id,
            costCenterName: values.costCenterName,
            costCenterCode: values.costCenterCode,
            disabled: values.disabled,
            comments: values.comments,
            useProjectBillingConfiguration: values.useProjectBillingConfiguration,
            minimumAssetImages: parseInt(values.minimumAssetImages, 10) || 0,
            ...emailListVariables(values),
          },
        });
        const updated = data?.updateCostCenter?.costCenter;
        if (!updated) {
          notifyError(
            data?.updateCostCenter?.errors?.map(({ message }) => message).join(", ") ||
              "Failed to update cost center"
          );
          return;
        }
        if (updated && onSaved) onSaved(updated);
      }
      onClose();
    } catch (error) {
      console.error("Error saving cost center:", error);
      notifyError(error.message || "Failed to save cost center");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleDisabled = async (values, setSubmitting, setFieldValue) => {
    const nextDisabled = !values.disabled;
    setSubmitting(true);
    try {
      const { data } = await updateCostCenter({
        variables: {
          id: values.id,
          disabled: nextDisabled,
        },
      });

      const updated = data?.updateCostCenter?.costCenter;
      if (updated && onSaved) onSaved(updated);

      setFieldValue("disabled", nextDisabled, false);
      setIsDisabled(nextDisabled);
    } catch (error) {
      console.error("Error toggling cost center disabled state:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={attemptClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ position: "relative", pb: 0, textAlign: "center" }}>
          <Typography fontSize={24} fontWeight={500} color="text.secondary">
            {isNew ? "Add" : "Edit"} Cost Center
          </Typography>
          {!isNew && isDisabled && (
            <Typography color="error.main" fontWeight={500}>
              Disabled
            </Typography>
          )}
          <IconButton
            aria-label="close"
            onClick={attemptClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={{
            useProjectBillingConfiguration: true,
            minimumAssetImages: 0,
            disabled: false,
            ...costCenter,
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, values, setFieldValue, setSubmitting }) => (
            <Form id="cost-center-form">
              <DialogContent>
                <Box pt={2} pb={3}>
                  <Stack spacing={2}>
                    <Input fullWidth label="Cost Center Name" name="costCenterName" showNAButton={false} />
                    <Input fullWidth label="Order Number" name="costCenterCode" showNAButton={false} />
                    <Input fullWidth label="Comments" name="comments" showNAButton={false} />
                    <Input 
                      fullWidth 
                      label="Minimum Item Pictures" 
                      name="minimumAssetImages" 
                      type="number"
                      inputProps={{ min: 0 }}
                      showNAButton={false}
                    />
                    <Button
                      fullWidth
                      variant="outlined"
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transform: emailSectionExpanded ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.3s",
                          }}
                        />
                      }
                      onClick={() => setEmailSectionExpanded(!emailSectionExpanded)}
                      sx={{ mt: 1 }}
                    >
                      Use Dedicated Email Lists for This Cost Center
                    </Button>
                    <Collapse in={emailSectionExpanded} timeout="auto" unmountOnExit>
                      <Stack spacing={2} sx={{ pt: 2 }}>
                        {EMAIL_LIST_FIELDS.map(({ name, label }) => (
                          <Input
                            key={name}
                            fullWidth
                            type="autoExpand"
                            name={name}
                            label={label}
                            placeholder="Separate emails with commas"
                            showNAButton={false}
                          />
                        ))}
                      </Stack>
                    </Collapse>
                  </Stack>
                </Box>
              </DialogContent>
              <DialogActions sx={{ display: "flex", justifyContent: "space-between", px: 3, py: 2 }}>
                {!isNew && (
                  <Button
                    color={values.disabled ? "primary" : "error"}
                    variant="text"
                    onClick={() => handleToggleDisabled(values, setSubmitting, setFieldValue)}
                    disabled={isSubmitting}
                  >
                    {values.disabled ? "Activate" : "Disable"}
                  </Button>
                )}
                {isNew && <Box />}
                <Box display="flex" gap={1}>
                  <Button variant="outlined" onClick={attemptClose}>
                    Cancel
                  </Button>
                  <Button
                    form="cost-center-form"
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    Save
                  </Button>
                </Box>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
      {showDialog && (
        <UnsavedChangesDialog
          open={showDialog}
          onCancel={cancelDiscard}
          onDiscard={confirmDiscard}
          onSave={confirmSave}
          saving={unsavedSaving}
        />
      )}
    </>
  );
}
